/**
 * Module valuation defines top-level valuation response schemas, including metadata,
 * inspection, reporting, and internal and external valuation variants.
 *
 * Economic-unit composition is defined in valuation-details.ts. Domain schemas are defined
 * in the corresponding valuation modules.
 */
import { z } from 'zod';
import { AppraiserAssignmentSchema } from './appraiser-assignment';
import { ISODateSchema, ISODateTimeSchema, UUIDSchema } from './common';
import { AreaValueSchema, PercentageSchema } from './measurements';
import { MonetaryValueSchema, SquareMetreMoneyPerAreaValueSchema } from './money';
import { RootTenantSummaryItemSchema, ValueOverviewTenantSummarySchema } from './tenant-summary';
import { MonetaryValueWithTrendSchema } from './trend-values';
import { ExternalValuationEconomicUnitSchema, SummaryRisksSchema, SwotAnalysisSchema, TextblockSchema, ValuationEconomicUnitSchema, ValXmlFileSchema, } from './valuation-details';
import { ExternalReportTypeSchema, ExternalValuationSubmissionTriggerSchema, ReportTypeSchema, ValuationMethodSchema, ValuationStatusSchema, } from './valuation-primitives';
import { FactsSchema } from './valuation-facts';
import { LandDescriptionSchema, StandardLandValueZoneSchema } from './valuation-property';
import { ExistingDiscountedCashFlowResultSchema } from './value-overview';
import { ValuationComparablesSchema } from './valuation-comparables';
import { ValuationSensitivityMatrixSchema } from './valuation-sensitivity';
export { ValuationStatusSchema, ValuationMethodSchema, ValuationTypeSchema, ReportTypeSchema, ExternalReportTypeSchema, ExternalValuationSubmissionTriggerSchema, } from './valuation-primitives';
export { ValuationComparablesSchema } from './valuation-comparables';
export * from './valuation-sensitivity';
export const SmartPdfSubmissionTargetSchema = z.enum(['draft', 'final']);
export const SmartPdfSubmissionResultSchema = z.object({
    source_valuation_uuid: UUIDSchema,
    valuation_uuid: UUIDSchema,
    target: SmartPdfSubmissionTargetSchema,
});
const AiWarningValueSchema = z.object({
    uuid: z.string(),
    warning_type: z.string(),
    message: z.object({ de: z.string(), en: z.string() }),
    context: z.record(z.string(), z.unknown()),
    created_at: z.string(),
    resolved_at: z.string().nullable().optional(),
});
export const InspectorSchema = z.object({
    name: z.string().optional(),
    role: z.string().optional(),
    company: z.string().optional(),
    user_id: z.number().optional(),
});
export const InspectionSchema = z.object({
    primary_inspector: InspectorSchema,
    secondary_inspectors: InspectorSchema.array(),
});
const ValXmlExportCapabilitiesSchema = z.object({ valxml_v1_1: z.boolean() });
export const ValuationCapabilitiesSchema = z.object({
    valxml_exportable: ValXmlExportCapabilitiesSchema,
});
export const AcquisitionCostsSchema = z.object({
    property_transfer_tax: PercentageSchema.optional(),
    notary_and_land_registration_fees: PercentageSchema.optional(),
    marketing_costs: PercentageSchema.optional(),
});
export const AssumptionsSchema = z.object({
    has_assumptions: z.boolean(),
    has_stipulations: z.boolean(),
    text: z.string(),
    stipulation_text: z.string(),
});
export const ValuationQualitySchema = z.enum([
    'excellent',
    'very_good',
    'good',
    'above_average',
    'slightly_above_average',
    'average',
    'slightly_below_average',
    'below_average',
    'mediocre',
    'bad',
    'catastrophic',
]);
export const ValuationRatingSummarySchema = z.object({
    total: z.number().optional(),
    structures: z.number().optional(),
    location: z.number().optional(),
    micro_location: z.number().optional(),
    macro_location: z.number().optional(),
    real_estate_market: z.number().optional(),
    real_estate_economics: z.number().optional(),
    real_estate_structure: z.number().optional(),
    rent_situation: ValuationQualitySchema.optional(),
});
const ExternalValuationQualitySchema = z.union([ValuationQualitySchema, z.literal('')]);
export const ExternalValuationRatingsSchema = z.object({
    structures: ExternalValuationQualitySchema,
    location: ExternalValuationQualitySchema,
    rent_situation: ValuationQualitySchema,
});
export const ValuationMethodValuesSchema = z.partialRecord(ValuationMethodSchema, MonetaryValueSchema);
export const ValuationReportFileSchema = z.object({
    uuid: UUIDSchema,
    name: z.string(),
    created_on: ISODateTimeSchema.nullable(),
    category: z.literal('report'),
    content_type: z.string().nullable(),
    url: z.string().nullable(),
    tags: z.string().array().nullable(),
});
export const ValuationSourceAttachmentSchema = ValuationReportFileSchema.extend({
    category: z.literal('document'),
});
export const ValuationFinalReportFileSchema = z.object({
    uuid: UUIDSchema,
    name: z.string(),
    category: z.literal('report'),
    url: z.string(),
    content_type: z.literal('application/pdf'),
});
export const ValuationImageSchema = z.object({
    uuid: UUIDSchema,
    name: z.string(),
    created_on: ISODateTimeSchema,
    url: z.string(),
    tags: z.string().array().optional(),
    is_included_into_the_report: z.boolean(),
    category: z.literal('image'),
    thumbnail_url: z.string(),
});
const PageRangeSchema = z.object({ start: z.number(), end: z.number() });
const PageInformationSchema = z.object({
    ranges: PageRangeSchema.array(),
    num_pages: z.number(),
});
export const ValuationDocumentSchema = z.object({
    uuid: UUIDSchema,
    name: z.string(),
    created_on: ISODateTimeSchema,
    content_type: z.string().optional(),
    url: z.string(),
    tags: z.string().array().optional(),
    is_included_into_the_report: z.boolean(),
    category: z.literal('document'),
    legal_date: ISODateSchema.optional(),
    page_information: PageInformationSchema.optional(),
});
export const ValuationDocumentUploadResultSchema = ValuationDocumentSchema.extend({
    content_type: z.string(),
    tags: z.string().array(),
    legal_date: ISODateSchema.nullable(),
    page_information: PageInformationSchema.nullable(),
});
export const ValuationTenantOverviewSchema = ValueOverviewTenantSummarySchema;
const ReadBaseValuationSchema = z.object({
    ai_warnings: AiWarningValueSchema.array(),
    uuid: UUIDSchema,
    asset_uuid: UUIDSchema,
    order_uuid: UUIDSchema.optional(),
    deviation_valuation_uuid: UUIDSchema.optional(),
    status: ValuationStatusSchema,
    title: z.string().optional(),
    valuation_date: ISODateSchema.optional(),
    inspection_date: ISODateSchema.optional(),
    inspection: InspectionSchema.optional(),
    quality_assessment_date: ISODateSchema.optional(),
    report_date: ISODateSchema.optional(),
    appraiser_assignment: AppraiserAssignmentSchema.optional(),
    images: ValuationImageSchema.array(),
    documents: ValuationDocumentSchema.array(),
    report_file: ValuationFinalReportFileSchema.optional(),
    val_xml_file: ValXmlFileSchema.optional(),
    drafts: UUIDSchema.array(),
    created_on: ISODateTimeSchema.optional(),
    updated_on: ISODateTimeSchema.optional(),
    submitted_by: UUIDSchema.optional(),
    is_draft: z.boolean(),
    capabilities: ValuationCapabilitiesSchema,
    income_approach_sensitivity_matrix: ValuationSensitivityMatrixSchema.optional(),
    dcf: ExistingDiscountedCashFlowResultSchema.optional(),
    mortgage_lending_value: MonetaryValueSchema.optional(),
    market_value_by_valuation_method: ValuationMethodValuesSchema,
    mortgage_lending_value_by_valuation_method: ValuationMethodValuesSchema,
    market_value: MonetaryValueSchema,
    market_value_with_trend: MonetaryValueWithTrendSchema,
    transaction_costs: MonetaryValueSchema.optional(),
    ancillary_acquisition_costs: MonetaryValueSchema.optional(),
    market_value_including_transaction_costs: MonetaryValueSchema,
    investment_method_market_value: MonetaryValueSchema.optional(),
    gross_capital_value: MonetaryValueSchema.optional(),
    net_capital_value: MonetaryValueSchema.optional(),
    total_special_property_features: MonetaryValueSchema.optional(),
    market_value_per_rentable_area: SquareMetreMoneyPerAreaValueSchema.optional(),
    market_value_per_gross_floor_area: SquareMetreMoneyPerAreaValueSchema.optional(),
    market_value_per_land_area: SquareMetreMoneyPerAreaValueSchema.optional(),
    market_value_per_usage_area: SquareMetreMoneyPerAreaValueSchema.optional(),
    market_value_per_living_area: SquareMetreMoneyPerAreaValueSchema.optional(),
    comparables: ValuationComparablesSchema.optional(),
    wault: z.union([z.number(), z.literal('unlimited')]).optional(),
    land_area: AreaValueSchema.optional(),
    tenant_summary: ValuationTenantOverviewSchema.optional(),
    selected_valuation_methods: ValuationMethodSchema.array(),
    selected_mortgage_lending_valuation_methods: ValuationMethodSchema.array(),
    gross_floor_area: AreaValueSchema.optional(),
    rentable_area: AreaValueSchema.optional(),
    gross_yield: PercentageSchema.optional(),
    gross_yield_multiplier: z.number().optional(),
    net_yield_multiplier: z.number().optional(),
    gross_initial_yield: PercentageSchema.optional(),
    net_yield: PercentageSchema.optional(),
    net_initial_yield: PercentageSchema.optional(),
    cap_rate: PercentageSchema.optional(),
    discount_rate: PercentageSchema.optional(),
    acquisition_costs: AcquisitionCostsSchema.optional(),
});
const BaseValuationEconomicUnitSchema = ExternalValuationEconomicUnitSchema.omit({
    tenantsV3: true,
});
const EinwertValuationEconomicUnitsSchema = z.intersection(ValuationEconomicUnitSchema.array(), BaseValuationEconomicUnitSchema.array());
export const ExistingEinwertValuationSchema = ReadBaseValuationSchema.extend({
    valuation_type: z.literal('einwert'),
    mortgage_lending_value: MonetaryValueSchema,
    report_type: ReportTypeSchema,
    facts: FactsSchema,
    swot_analysis: SwotAnalysisSchema.optional(),
    summary_risks: SummaryRisksSchema.optional(),
    assumptions: AssumptionsSchema.optional(),
    flooding_risk: z.string().optional(),
    earthquake_zone: z.string().optional(),
    text_blocks: TextblockSchema.array(),
    economic_units: EinwertValuationEconomicUnitsSchema,
    inspection_description: z.string().optional(),
    land_description: LandDescriptionSchema.optional(),
    standard_land_value_zones: StandardLandValueZoneSchema.array().optional(),
    rating_summary: ValuationRatingSummarySchema.optional(),
});
const ExistingExternalOrHistoricalValuationSchema = ReadBaseValuationSchema.extend({
    calculated_gross_yield: z.number().optional(),
    calculated_gross_initial_yield: z.number().optional(),
    calculated_net_yield: z.number().optional(),
    calculated_net_initial_yield: z.number().optional(),
    calculated_cap_rate: z.number().optional(),
    calculated_gross_yield_multiplier: z.number().optional(),
    calculated_net_yield_multiplier: z.number().optional(),
    report_type: ExternalReportTypeSchema,
    tenant_summary_items: RootTenantSummaryItemSchema.array(),
    land_value: MonetaryValueSchema.optional(),
    land_mortgage_lending_value: MonetaryValueSchema.optional(),
    gross_rental_income: MonetaryValueSchema.optional(),
    income_market_value: MonetaryValueSchema.optional(),
    income_mortgage_lending_value: MonetaryValueSchema.optional(),
    cost_market_value: MonetaryValueSchema.optional(),
    cost_market_value_of_structures: MonetaryValueSchema.optional(),
    cost_mortgage_lending_value: MonetaryValueSchema.optional(),
    comparison_market_value: MonetaryValueSchema.optional(),
    comparison_mortgage_lending_value: MonetaryValueSchema.optional(),
    investment_method_market_value: MonetaryValueSchema.optional(),
    gross_capital_value: MonetaryValueSchema.optional(),
    net_capital_value: MonetaryValueSchema.optional(),
    total_special_property_features: MonetaryValueSchema.optional(),
    living_area: AreaValueSchema.optional(),
    usage_area: AreaValueSchema.optional(),
    economic_units: ExternalValuationEconomicUnitSchema.array(),
    rating_summary: ExternalValuationRatingsSchema.optional(),
    appraising_entity_name: z.string().optional(),
});
export const ExistingExternalValuationSchema = ExistingExternalOrHistoricalValuationSchema.extend({
    valuation_type: z.literal('external'),
    submission_trigger: ExternalValuationSubmissionTriggerSchema.nullable().optional(),
});
export const ExistingHistoricalValuationSchema = ExistingExternalOrHistoricalValuationSchema.extend({ valuation_type: z.literal('historical') });
export const ExistingValuationSchema = z.discriminatedUnion('valuation_type', [ExistingEinwertValuationSchema, ExistingExternalValuationSchema, ExistingHistoricalValuationSchema]);
export const ValuationListSchema = z.object({ valuations: ExistingValuationSchema.array() });
export const ValuationDraftResultSchema = z.object({ uuid: UUIDSchema });
export const ValuationCloneResultSchema = ValuationDraftResultSchema;
export const ExternalValuationImportResultSchema = z.object({
    uuid: UUIDSchema,
    asset_uuid: UUIDSchema,
    order_uuid: UUIDSchema.optional(),
    deviation_valuation_uuid: UUIDSchema.optional(),
});
